import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Activity, Trophy, Send, Plus } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle, Input } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { crmApi, ApiError } from '@solara/api-client';
import type { OpportunityDto, CrmActivityDto } from '@solara/api-client';

type OppWithExtras = OpportunityDto & { activities?: CrmActivityDto[]; productLines?: unknown[] };

const STAGE_TONES: Record<OpportunityDto['pipelineStage'], 'info' | 'warning' | 'success' | 'danger' | 'neutral'> = {
  DISCOVERY: 'info',
  DEMO: 'info',
  PROPOSAL: 'warning',
  NEGOTIATION: 'warning',
  CLOSED_WON: 'success',
  CLOSED_LOST: 'danger',
};

export default function OpportunityDetailPage() {
  const { t } = useTranslation('p1');
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [opp, setOpp] = useState<OppWithExtras | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activityType, setActivityType] = useState<CrmActivityDto['activityType']>('CALL');
  const [activityOutcome, setActivityOutcome] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (opportunityId) void load(opportunityId);
  }, [opportunityId]);

  async function load(id: string) {
    try {
      setOpp((await crmApi.getOpportunity(id)) as OppWithExtras);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('crm.loadOppFailed'));
    }
  }

  async function setStage(stage: OpportunityDto['pipelineStage']) {
    if (!opp) return;
    let reason: string | undefined;
    if (stage === 'CLOSED_LOST') {
      reason = window.prompt(t('crm.lossPromptShort')) ?? undefined;
      if (!reason) return;
    }
    setBusy(true);
    setError(null);
    try {
      await crmApi.setStage(opp.id, stage, reason);
      await load(opp.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('crm.stageChangeFailed'));
    } finally {
      setBusy(false);
    }
  }

  async function recordActivity() {
    if (!opp || !activityOutcome) return;
    setBusy(true);
    setError(null);
    try {
      await crmApi.createActivity({
        activityType,
        relatedOpportunityId: opp.id,
        outcome: activityOutcome,
      });
      setActivityOutcome('');
      await load(opp.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('crm.activityFailed'));
    } finally {
      setBusy(false);
    }
  }

  if (!opp) {
    return (
      <div className="p-6 text-sm text-fg-muted">
        {error ?? t('crm.loading')}
      </div>
    );
  }

  const amountAed = Number(opp.amountMinor) / 100;
  const weightedAed = (amountAed * opp.probability) / 100;
  const isClosed = opp.pipelineStage === 'CLOSED_WON' || opp.pipelineStage === 'CLOSED_LOST';

  return (
    <div className="space-y-6 max-w-5xl">
      <header className="flex items-center justify-between gap-4">
        <div>
          <Link to="/crm/opportunities" className="text-xs text-fg-muted hover:underline inline-flex items-center gap-1">
            <ArrowLeft className="h-3 w-3 rtl-mirror" /> {t('crm.backToPipeline')}
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{opp.dealName}</h1>
          <p className="text-fg-muted text-sm">
            {opp.company?.companyName ?? t('crm.unknownCompany')} ·{' '}
            {t('crm.probabilityPct', { value: opp.probability })}
          </p>
        </div>
        <Badge tone={STAGE_TONES[opp.pipelineStage]} className="text-xs">
          {t(`crm.stages.${opp.pipelineStage}`, { defaultValue: opp.pipelineStage })}
        </Badge>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle>{t('crm.dealFinancials')}</CardTitle>
        </CardHeader>
        <CardBody className="grid grid-cols-2 gap-3 text-sm">
          <Item label={t('crm.dealValueLabel')} value={`${amountAed.toLocaleString()} ${opp.currency}`} />
          <Item label={t('crm.weightedForecastLabel')} value={`${weightedAed.toLocaleString()} ${opp.currency}`} highlight />
          <Item label={t('crm.expectedCloseLabel')} value={new Date(opp.expectedCloseDate).toLocaleDateString()} />
          <Item label={t('crm.packageTier')} value={opp.packageTier ?? '—'} />
          {opp.winReason && <Item label={t('crm.winReasonLabel')} value={opp.winReason} />}
          {opp.lossReason && <Item label={t('crm.lossReasonLabel')} value={opp.lossReason} />}
        </CardBody>
      </Card>

      {!isClosed && (
        <Card className="rounded-2xl">
          <CardHeader>
            <CardTitle>{t('crm.advancePipeline')}</CardTitle>
          </CardHeader>
          <CardBody className="flex flex-wrap gap-2">
            {(['DEMO', 'PROPOSAL', 'NEGOTIATION'] as const)
              .filter((s) => s !== opp.pipelineStage)
              .map((s) => (
                <Button key={s} variant="secondary" onClick={() => void setStage(s)} disabled={busy}>
                  → {t(`crm.stages.${s}`, { defaultValue: s })}
                </Button>
              ))}
            <Button onClick={() => void setStage('CLOSED_WON')} disabled={busy}>
              <Trophy className="mr-2 h-3 w-3" />
              {t('crm.closedWonLabel')}
            </Button>
            <Button variant="ghost" onClick={() => void setStage('CLOSED_LOST')} disabled={busy}>
              {t('crm.closedLostLabel')}
            </Button>
          </CardBody>
        </Card>
      )}

      {opp.pipelineStage === 'CLOSED_WON' && (
        <Card className="border-success/40 bg-success/5 rounded-2xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-success">
              <Trophy className="h-4 w-4" />
              {t('crm.closedWonLabel')}
            </CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 text-sm">
            <p>{t('crm.closedWonReady')}</p>
            <Button onClick={() => void handOffToOnboarding(opp.id, t)} disabled={busy}>
              <Send className="mr-2 h-3 w-3" />
              {t('crm.handOffButton')}
            </Button>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" />
            {t('crm.activityHistoryTitle')}
          </CardTitle>
        </CardHeader>
        <CardBody className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <select
              className="rounded border border-border bg-surface px-3 py-2 text-sm"
              value={activityType}
              onChange={(e) => setActivityType(e.target.value as CrmActivityDto['activityType'])}
            >
              {(['CALL', 'MEETING', 'EMAIL', 'DEMO', 'PROPOSAL', 'NOTE'] as const).map((ty) => (
                <option key={ty} value={ty}>
                  {ty}
                </option>
              ))}
            </select>
            <Input
              value={activityOutcome}
              onChange={(e) => setActivityOutcome(e.target.value)}
              placeholder={t('crm.outcomePlaceholder')}
              className="flex-1 min-w-[16rem]"
            />
            <Button onClick={() => void recordActivity()} disabled={busy || !activityOutcome}>
              <Plus className="mr-1 h-3 w-3" />
              {t('crm.logActivity')}
            </Button>
          </div>

          {opp.activities && opp.activities.length > 0 ? (
            <ul className="divide-y divide-border">
              {opp.activities.map((a) => (
                <li key={a.id} className="py-2">
                  <p className="font-medium">{a.activityType}</p>
                  <p className="text-xs text-fg-muted">
                    {new Date(a.activityTimestamp).toLocaleString()}
                    {a.outcome && <span> — {a.outcome}</span>}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-fg-muted">{t('crm.noActivities')}</p>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Item({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className={highlight ? 'col-span-2 rounded bg-surface-muted px-3 py-2' : ''}>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="font-mono text-sm">{value}</dd>
    </div>
  );
}

async function handOffToOnboarding(
  opportunityId: string,
  t: (k: string, vars?: Record<string, string | number>) => string,
) {
  const { crmApi } = await import('@solara/api-client');
  try {
    const result = await crmApi.handOffToOnboarding(opportunityId);
    window.alert(t('crm.tenantCreated', { id: result.tenantId }));
  } catch (err) {
    window.alert(t('crm.handOffFailed', { message: (err as Error).message }));
  }
}
